import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchInstructors } from "../../store/instructorsSlice";
import { addInstructorBlackouts } from "../../api/instructorsService";
import {
   BUSY_KEYS_MODE,
   isoForDbMatchLocalHour,
   toUtcIsoFromMoldova,
} from "../../utils/moldovaTime";

const SLOT_TIMES = [
   "07:00",
   "08:30",
   "10:00",
   "11:30",
   "13:30",
   "15:00",
   "16:30",
   "18:00",
];

const DEFAULT_TARGET = 3000;
const DEFAULT_BATCH_SIZE = 80;
const DEFAULT_BATCH_DELAY_MS = 350;

function sleep(ms) {
   return new Promise((resolve) => setTimeout(resolve, ms));
}

function shuffleInPlace(arr) {
   for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
   }
   return arr;
}

function toPositiveInt(v) {
   const n = Number(v);
   return Number.isInteger(n) && n > 0 ? n : null;
}

function monthInputValue(date = new Date()) {
   const y = date.getFullYear();
   const m = String(date.getMonth() + 1).padStart(2, "0");
   return `${y}-${m}`;
}

function parseMonthValue(value) {
   const match = String(value || "").match(/^(\d{4})-(\d{2})$/);
   if (!match) return null;
   const year = Number(match[1]);
   const month = Number(match[2]);
   if (!year || month < 1 || month > 12) return null;
   return { year, month };
}

function workingDaysInMonth(year, month) {
   const days = [];
   const last = new Date(year, month, 0).getDate();
   for (let d = 1; d <= last; d += 1) {
      const date = new Date(year, month - 1, d, 12, 0, 0, 0);
      if (date.getDay() === 0) continue;
      days.push(date);
   }
   return days;
}

function buildStartTimeIso(localDateObj, timeHHMM) {
   const utcIso = toUtcIsoFromMoldova(localDateObj, timeHHMM);
   return BUSY_KEYS_MODE === "local-match"
      ? isoForDbMatchLocalHour(utcIso)
      : utcIso;
}

/**
 * Plan sparse de blocări: max 1 blackout / instructor / slot.
 * Densitate ~55% pe încercări random, ca să umple bine fără 100% plin.
 */
export function planSparseMonthBlackouts({
   year,
   month,
   targetCount,
   instructors,
}) {
   const days = workingDaysInMonth(year, month);
   const instructorPool = [...(instructors || [])].filter(
      (i) => toPositiveInt(i?.id) != null,
   );
   if (!instructorPool.length || !days.length) return [];

   const candidates = [];
   for (const day of days) {
      for (const time of SLOT_TIMES) {
         for (const instructor of instructorPool) {
            const instructorId = toPositiveInt(instructor.id);
            if (!instructorId) continue;
            candidates.push({
               instructorId,
               dateTime: buildStartTimeIso(day, time),
               dateStr: `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`,
               time,
            });
         }
      }
   }

   shuffleInPlace(candidates);
   const hardMax = candidates.length;
   const target = Math.min(Math.max(0, Number(targetCount) || 0), hardMax);
   const planned = [];
   const used = new Set();

   for (const item of candidates) {
      if (planned.length >= target) break;
      // densitate moderată+ : ~55% acceptare pe listă amestecată
      if (Math.random() > 0.55 && planned.length < target * 0.85) continue;
      const key = `${item.instructorId}|${item.dateStr}|${item.time}`;
      if (used.has(key)) continue;
      used.add(key);
      planned.push({
         instructorId: item.instructorId,
         dateTime: item.dateTime,
      });
   }

   // dacă densitatea a rămas sub țintă, completează din restul candidaților
   if (planned.length < target) {
      for (const item of candidates) {
         if (planned.length >= target) break;
         const key = `${item.instructorId}|${item.dateStr}|${item.time}`;
         if (used.has(key)) continue;
         used.add(key);
         planned.push({
            instructorId: item.instructorId,
            dateTime: item.dateTime,
         });
      }
   }

   return planned;
}

export default function TestBlackoutSeeder() {
   const dispatch = useDispatch();
   const instructorsRaw = useSelector((s) => s.instructors?.list || []);
   const instructorsLoading = useSelector((s) => s.instructors?.loading);

   const [monthValue, setMonthValue] = useState(() => monthInputValue());
   const [targetCount, setTargetCount] = useState(DEFAULT_TARGET);
   const [batchSize, setBatchSize] = useState(DEFAULT_BATCH_SIZE);
   const [batchDelayMs, setBatchDelayMs] = useState(DEFAULT_BATCH_DELAY_MS);

   const [running, setRunning] = useState(false);
   const [done, setDone] = useState(0);
   const [failed, setFailed] = useState(0);
   const [total, setTotal] = useState(0);
   const [status, setStatus] = useState("idle");
   const [lastError, setLastError] = useState("");
   const [plannedPreview, setPlannedPreview] = useState(0);

   const abortRef = useRef(false);

   useEffect(() => {
      if (!instructorsRaw.length) dispatch(fetchInstructors());
   }, [dispatch, instructorsRaw.length]);

   const instructors = useMemo(() => {
      return (instructorsRaw || []).filter((i) => toPositiveInt(i?.id) != null);
   }, [instructorsRaw]);

   const monthParsed = useMemo(
      () => parseMonthValue(monthValue),
      [monthValue],
   );

   const theoreticalMax = useMemo(() => {
      if (!monthParsed) return 0;
      const days = workingDaysInMonth(monthParsed.year, monthParsed.month);
      return instructors.length * days.length * SLOT_TIMES.length;
   }, [monthParsed, instructors.length]);

   useEffect(() => {
      if (!monthParsed || running) return;
      const plan = planSparseMonthBlackouts({
         year: monthParsed.year,
         month: monthParsed.month,
         targetCount: Math.min(Number(targetCount) || 0, theoreticalMax),
         instructors,
      });
      setPlannedPreview(plan.length);
   }, [monthParsed, targetCount, theoreticalMax, instructors, running]);

   const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;

   const stop = useCallback(() => {
      abortRef.current = true;
      setStatus("stopping");
   }, []);

   const start = useCallback(async () => {
      if (running) return;
      if (!monthParsed) {
         setLastError("Selectează o lună validă.");
         return;
      }
      if (!instructors.length) {
         setLastError("Nu există instructori în sistem.");
         return;
      }

      const wanted = Math.min(
         Math.max(1, Number(targetCount) || DEFAULT_TARGET),
         theoreticalMax || Number(targetCount) || DEFAULT_TARGET,
      );

      const planned = planSparseMonthBlackouts({
         year: monthParsed.year,
         month: monthParsed.month,
         targetCount: wanted,
         instructors,
      });

      if (!planned.length) {
         setLastError(
            "Nu am putut genera un plan de blocări pentru lună.",
         );
         return;
      }

      abortRef.current = false;
      setRunning(true);
      setStatus("running");
      setDone(0);
      setFailed(0);
      setTotal(planned.length);
      setLastError("");
      setPlannedPreview(planned.length);

      const chunkSize = Math.max(1, Number(batchSize) || DEFAULT_BATCH_SIZE);
      const delay = Math.max(0, Number(batchDelayMs) || 0);
      let okCount = 0;
      let failCount = 0;

      for (let i = 0; i < planned.length; i += chunkSize) {
         if (abortRef.current) break;

         const chunk = planned.slice(i, i + chunkSize);
         try {
            await addInstructorBlackouts({
               blackouts: chunk.map((item) => ({
                  instructorId: item.instructorId,
                  dateTime: item.dateTime,
               })),
            });
            okCount += chunk.length;
         } catch (err) {
            failCount += chunk.length;
            const msg = err?.message || String(err || "");
            if (msg) setLastError(msg);
         }

         setDone(okCount);
         setFailed(failCount);

         if (abortRef.current) break;
         if (i + chunkSize < planned.length && delay > 0) {
            await sleep(delay);
         }
      }

      setRunning(false);
      setStatus(abortRef.current ? "stopped" : "done");
   }, [
      batchDelayMs,
      batchSize,
      instructors,
      monthParsed,
      running,
      targetCount,
      theoreticalMax,
   ]);

   return (
      <div className="adminOther__seeder">
         <div className="adminOther__seederHead">
            <div>
               <p className="adminOther__linkMeta">Testare</p>
               <h3 className="adminOther__seederTitle">
                  Seed blocări pe lună
               </h3>
               <p className="adminOther__seederHint">
                  Generează blocări pe instructorii existenți, în batch-uri
                  (span), ca să umpli graficul dens pentru stres-test.
               </p>
            </div>
         </div>

         <div className="adminOther__seederGrid">
            <label className="adminOther__seederField">
               <span>Luna</span>
               <input
                  type="month"
                  value={monthValue}
                  onChange={(e) => setMonthValue(e.target.value)}
                  disabled={running}
               />
            </label>

            <label className="adminOther__seederField">
               <span>Țintă blocări</span>
               <input
                  type="number"
                  min={1}
                  max={Math.max(theoreticalMax, 1)}
                  step={100}
                  value={targetCount}
                  onChange={(e) => setTargetCount(Number(e.target.value) || 0)}
                  disabled={running}
               />
            </label>

            <label className="adminOther__seederField">
               <span>Batch size (span)</span>
               <input
                  type="number"
                  min={1}
                  max={200}
                  value={batchSize}
                  onChange={(e) => setBatchSize(Number(e.target.value) || 1)}
                  disabled={running}
               />
            </label>

            <label className="adminOther__seederField">
               <span>Pauză între batch-uri (ms)</span>
               <input
                  type="number"
                  min={0}
                  max={5000}
                  step={50}
                  value={batchDelayMs}
                  onChange={(e) =>
                     setBatchDelayMs(Number(e.target.value) || 0)
                  }
                  disabled={running}
               />
            </label>
         </div>

         <div className="adminOther__seederStats">
            <span>Instructori: {instructors.length}</span>
            <span>Capacitate lună: {theoreticalMax}</span>
            <span>Plan estimat: {plannedPreview}</span>
         </div>

         <div className="adminOther__seederProgress">
            <div
               className="adminOther__seederProgressBar"
               style={{ width: `${progressPct}%` }}
            />
         </div>

         <div className="adminOther__seederFooter">
            <div className="adminOther__seederStatus">
               {instructorsLoading
                  ? "Se încarcă instructorii…"
                  : status === "idle"
                    ? "Pregătit."
                    : status === "running"
                      ? `Se creează… ${done}/${total} (eșuate: ${failed})`
                      : status === "stopping"
                        ? `Se oprește… ${done}/${total}`
                        : status === "stopped"
                          ? `Oprit. Create: ${done}, eșuate: ${failed}`
                          : `Gata. Create: ${done}, eșuate: ${failed}`}
               {lastError ? (
                  <div className="adminOther__seederError">{lastError}</div>
               ) : null}
            </div>

            <div className="adminOther__seederActions">
               {!running ? (
                  <button
                     type="button"
                     className="adminOther__seederBtn adminOther__seederBtn--primary"
                     onClick={start}
                     disabled={instructorsLoading || !instructors.length}
                  >
                     Generează blocări
                  </button>
               ) : (
                  <button
                     type="button"
                     className="adminOther__seederBtn adminOther__seederBtn--danger"
                     onClick={stop}
                  >
                     Oprește
                  </button>
               )}
            </div>
         </div>
      </div>
   );
}
